// src/features/channelManager/components/ChannelTable.jsx
import React, { useMemo } from "react";
import { Table, Tag, Button, Space, Popconfirm, Tooltip, Badge, Avatar } from "antd";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { Link2, Settings, RefreshCw, Power, ExternalLink, Activity, CheckCircle } from "react-feather";

const statusMeta = (row) => {
  const connected = !!row?.is_connected;

  // IMPORTANT: if backend returns is_active undefined for not-connected rows,
  // treat it as "not connected" (not disabled).
  const active = row?.is_active === undefined ? true : row?.is_active !== false;

  const syncStatus = String(row?.sync_status || row?.last_sync_status || "").toLowerCase();
  const hasIssue = !!row?.error_message || syncStatus === "failed";

  if (connected && !active) return { text: "Disabled", color: "default", dot: "grey" };
  if (connected && !hasIssue) return { text: "Connected", color: "success", dot: "green" };
  if (connected && hasIssue) return { text: "Attention", color: "error", dot: "red" };
  if (syncStatus === "pending") return { text: "Pending", color: "warning", dot: "gold" };
  return { text: "Not connected", color: "default", dot: "grey" };
};

export default function ChannelTable({
  channels = [],
  loading,
  actionLoading = {},       // ✅ NEW: { [code]: { sync, active, mapping, reconcile } }
  onSync,
  onOpenMapping,
  onConnectChannel,         // (code) => void
  onToggleActive,           // (row, nextActive:boolean) => void
  onReconcileChannel,       // (row) => void
}) {
  const navigate = useNavigate();

  const data = useMemo(() => (channels || []).map((c) => ({ ...c, key: c.code })), [channels]);

  const columns = [
    {
      title: "CHANNEL NAME",
      dataIndex: "name",
      render: (name, r) => {
        const s = statusMeta(r);
        const active = r?.is_active === undefined ? true : r?.is_active !== false;
        const busy = actionLoading?.[r.code] || {};

        return (
          <div style={{ display: "flex", gap: 16, alignItems: "center", opacity: busy.active ? 0.8 : 1 }}>
            <div style={{ position: "relative" }}>
              <Avatar
                shape="square"
                size={48}
                src={r.logo}
                style={{
                  backgroundColor: "#F4F5F7",
                  border: "1px solid #ebecf0",
                  color: "#172B4D",
                  fontWeight: 800,
                  opacity: active ? 1 : 0.55,
                  filter: active ? "none" : "grayscale(0.3)",
                }}
              >
                {(name || r.code || "?").slice(0, 1).toUpperCase()}
              </Avatar>
              <Badge
                color={s.dot}
                style={{
                  position: "absolute",
                  bottom: -5,
                  right: -5,
                  boxShadow: "0 0 0 2px #fff",
                }}
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column" }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: "#172B4D" }}>
                {name || r.code}
              </span>

              <div style={{ fontSize: 12, color: "#6B778C", marginTop: 2 }}>
                {r.api_provider || "Direct"} {r.hotel_id ? `• ID: ${r.hotel_id}` : ""}
              </div>

              {/* Show guidance when remote deactivate cannot work without channel id */}
              {!!r.property_id && r.is_connected && !r.channex_channel_id && (
                <div style={{ fontSize: 11, color: "#B45309", marginTop: 4 }}>
                  Setup incomplete • click <b>Finish setup</b> (links Channex channel id)
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: "STATUS",
      width: 150,
      render: (_, r) => {
        const s = statusMeta(r);
        return (
          <Tag color={s.color} style={{ borderRadius: 12, fontWeight: 700 }}>
            {s.text.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: "LAST SYNC",
      dataIndex: "last_synced_at",
      width: 180,
      render: (d, r) => (
        <div style={{ color: "#172B4D" }}>
          {d ? moment(d).fromNow() : "—"}
          {r.last_sync_status === "failed" && <div style={{ color: "red", fontSize: 11 }}>Sync Failed</div>}
        </div>
      ),
    },
{
  title: "ACTIONS",
  align: "right",
  render: (_, r) => {
    const connected = !!r.is_connected;
    const active = r?.is_active === undefined ? true : r?.is_active !== false;

    const busy = actionLoading?.[r.code] || {};
    const anyBusy = !!(busy.sync || busy.active || busy.mapping || busy.reconcile);

    const hasExistingEntry = !!r.property_id || !!r.hotel_id || !!r.channex_channel_id;

    // 1) NOT CONNECTED
    if (!connected) {
      return (
        <Space size={8}>
          {hasExistingEntry ? (
            <Button
              type="primary"
              size="small"
              icon={<Link2 size={14} />}
              loading={!!busy.active}
              disabled={anyBusy}
              onClick={() => onToggleActive?.(r, true)}
              style={{ borderRadius: 8, fontWeight: 700 }}
            >
              Reconnect
            </Button>
          ) : (
            <Button
              type="primary"
              size="small"
              icon={<Link2 size={14} />}
              disabled={anyBusy}
              onClick={() => onConnectChannel?.(r.code)}
              style={{ borderRadius: 8, fontWeight: 700 }}
            >
              Connect
            </Button>
          )}
        </Space>
      );
    }

    // 2) CONNECTED BUT DISABLED → keep UI simple
    if (connected && !active) {
      return (
        <Space size={8}>
          {/* Finish setup only when channel id missing */}
          {!!r.property_id && !r.channex_channel_id && (
            <Tooltip title="Fetch channel ID from Channex (required for remote disable/enable)">
              <Button
                size="small"
                icon={<CheckCircle size={14} />}
                loading={!!busy.reconcile}
                disabled={anyBusy}
                onClick={() => onReconcileChannel?.(r)}
              >
                Finish setup
              </Button>
            </Tooltip>
          )}

          {/* Mapping is still allowed even if disabled (optional) */}
          <Tooltip title="Mapping (Channex)">
            <Button
              size="small"
              icon={<ExternalLink size={14} />}
              loading={!!busy.mapping}
              disabled={anyBusy || !r.property_id}
              onClick={() => onOpenMapping?.(r)}
            />
          </Tooltip>

          {/* Primary action */}
          <Button
            type="primary"
            size="small"
            icon={<Link2 size={14} />}
            loading={!!busy.active}
            disabled={anyBusy}
            onClick={() => onToggleActive?.(r, true)}
            style={{ borderRadius: 8, fontWeight: 700 }}
          >
            Reconnect
          </Button>
        </Space>
      );
    }

    // 3) CONNECTED + ACTIVE → full actions
    return (
      <Space size={8}>
        {/* Finish setup = reconcile channel id after iframe-created channel */}
        {!!r.property_id && !r.channex_channel_id && (
          <Tooltip title="Fetch channel ID from Channex (required for remote disable/enable)">
            <Button
              size="small"
              icon={<CheckCircle size={14} />}
              loading={!!busy.reconcile}
              disabled={anyBusy}
              onClick={() => onReconcileChannel?.(r)}
            >
              Finish setup
            </Button>
          </Tooltip>
        )}

        <Tooltip title="Sync Now">
          <Button
            size="small"
            icon={<RefreshCw size={14} />}
            loading={!!busy.sync}
            disabled={anyBusy}
            onClick={() => onSync?.(r)}
          />
        </Tooltip>

        <Tooltip title="Mapping (Channex)">
          <Button
            size="small"
            icon={<ExternalLink size={14} />}
            loading={!!busy.mapping}
            disabled={anyBusy || !r.property_id}
            onClick={() => onOpenMapping?.(r)}
          />
        </Tooltip>

        <Tooltip title="Settings">
          <Button
            size="small"
            icon={<Settings size={14} />}
            disabled={anyBusy}
            onClick={() => navigate(`/channel-manager/settings/${r.code}`)}
          />
        </Tooltip>

        {/* Keep ONE disable control only (Power) */}
        <Popconfirm
          title="Disable this channel?"
          description="This will disable sync in NetShine and deactivate it in Channex. Mapping remains saved."
          onConfirm={() => onToggleActive?.(r, false)}
          okText="Disable"
          cancelText="Cancel"
          disabled={anyBusy}
        >
          <Button size="small" danger icon={<Power size={14} />} disabled={anyBusy} />
        </Popconfirm>
      </Space>
    );
  },
}

  ];

  return (
    <Table
      columns={columns}
      dataSource={data}
      loading={loading}
      rowKey="code"
      pagination={{ pageSize: 8, hideOnSinglePage: true }}
    />
  );
}
